/**
 * What a visitor sees before they sign in.
 *
 * The app used to open on a sign-in card, which asks somebody to prove who they
 * are before telling them what this is. That reads as an internal tool left
 * exposed rather than a product, and it gives a member sent a link no way to
 * understand what they have been sent.
 *
 * Everything here is drawn rather than photographed: the illustrations are
 * inline SVG built from the same green as the app, so the page has no external
 * requests, no image weight and nothing to go stale when the brand moves.
 */

export function MarketingPage({ onSignIn }: { onSignIn: () => void }) {
  return (
    <div className="marketing">
      <header className="mk-nav">
        <span className="brand-lockup">
          <span className="brand-mark">
            <img className="brand-logo" src="/logo-mark.png" alt="" width={20} height={20} />
            Concourse Works
          </span>
          <span className="brand-tool">Bay One</span>
        </span>
        <button className="primary mk-nav-cta" onClick={onSignIn}>
          Sign in
        </button>
      </header>

      <section className="mk-hero">
        <div className="mk-hero-copy">
          <p className="mk-eyebrow">Diagnosis, explained and kept</p>
          <h1>
            Find out what is actually wrong, and what it will take to fix.
          </h1>
          <p className="mk-lede">
            Bay One reads every fault the car will report, explains what each one means, identifies the parts it
            points to, and keeps the whole thing against that vehicle. Open recalls and the service that is coming due
            sit alongside it, so what is wrong now and what is next are in the same place. One car you own or fifty
            you look after.
          </p>
          <div className="mk-actions">
            <button className="primary" onClick={onSignIn}>
              Sign in
            </button>
            <a className="mk-secondary" href="#how">
              See how it works
            </a>
          </div>
          <p className="mk-fineprint">By invitation while we are getting started.</p>
        </div>

        <div className="mk-hero-art" aria-hidden="true">
          <ConvergenceArt />
        </div>
      </section>

      <section className="mk-claims">
        <h2>What a code reader will not do</h2>
        <p className="mk-section-lede">
          Any dongle reads a fault code and hands it back. Four things happen here that do not happen there.
        </p>

        <div className="mk-claim-grid">
          <article className="mk-claim">
            <span className="mk-claim-n">01</span>
            <HistoryArt />
            <h3>Every scan stays with the car</h3>
            <p>
              Not with the phone that took it or the technician who was on shift. A fault that appeared in March is
              still there in September, so <em>has this happened before</em> is a question with an answer.
            </p>
          </article>

          <article className="mk-claim">
            <span className="mk-claim-n">02</span>
            <ConvergeArt />
            <h3>One scan, four answers</h3>
            <p>
              The codes, the open recalls, the service that is due against real mileage, and the parts to order for
              what was actually found. Against one VIN, not four separate lookups.
            </p>
          </article>

          <article className="mk-claim">
            <span className="mk-claim-n">03</span>
            <TranslateArt />
            <h3>The whole scan, translated</h3>
            <p>
              Not a canned paragraph attached to a code. A write-up of this scan — the unfinished self-tests, the
              freeze frame, the distance since the fault appeared — with every finding rated for urgency, in your
              units.
            </p>
          </article>

          <article className="mk-claim">
            <span className="mk-claim-n">04</span>
            <ScopeArt />
            <h3>Owners see their own car. Only theirs.</h3>
            <p>
              Access follows the car, not the company holding it. One login covers your vehicles wherever they are,
              and nothing belonging to anybody else is visible.
            </p>
          </article>
        </div>

        <p className="mk-aside">
          Two people can read the same scan: a member understands their car without learning what P0420 means, and a
          technician gets a triaged list instead of a wall of hex.
        </p>
      </section>

      <section className="mk-audiences">
        <h2>One car, or a hundred</h2>
        <p className="mk-section-lede">
          The same scan answers two different questions depending on whose car it is. So there are two kinds of
          account, rather than one with pieces hidden.
        </p>

        <div className="mk-cards">
          <article className="mk-card">
            <ShopArt />
            <h3>If you look after cars</h3>
            <p>
              A garage, a storage facility, a fleet, or a collection you manage. Scan anything you hold over
              Bluetooth, Wi-Fi or a USB reader, clear codes, record mileage, mark work done. Every vehicle in one
              list, with the ones needing attention at the top.
            </p>
            <ul>
              <li>Owners, technicians and read-only staff</li>
              <li>Parts matched to the codes that were found</li>
              <li>Nobody outside your organisation sees any of it</li>
            </ul>
          </article>

          <article className="mk-card">
            <MemberArt />
            <h3>If it is your car</h3>
            <p>
              Your own vehicles and nothing else. What the last scan found, what service is due, whether there is an
              open recall, and what any of it actually means — without having to know what P0420 stands for.
            </p>
            <ul>
              <li>One account, even if your cars live in different places</li>
              <li>A history that follows the car, not the phone that scanned it</li>
              <li>Nobody else can see your vehicles</li>
            </ul>
          </article>
        </div>
      </section>

      <section className="mk-how" id="how">
        <h2>How a scan works</h2>
        <ol className="mk-steps">
          <li>
            <span className="mk-step-n">1</span>
            <div>
              <h3>Plug in and connect</h3>
              <p>
                A standard OBD-II reader over Bluetooth, Wi-Fi or USB. There is a simulator too, so the whole thing can
                be shown without a car.
              </p>
            </div>
          </li>
          <li>
            <span className="mk-step-n">2</span>
            <div>
              <h3>Read everything the car will say</h3>
              <p>
                Stored, pending and permanent codes, the freeze frame taken when the fault appeared, emissions
                readiness, the vehicle's own test results, and the VIN.
              </p>
            </div>
          </li>
          <li>
            <span className="mk-step-n">3</span>
            <div>
              <h3>Check it against what is known</h3>
              <p>
                The VIN is decoded, recalls are matched, service intervals are judged against mileage, and parts are
                looked up for the codes that were found.
              </p>
            </div>
          </li>
          <li>
            <span className="mk-step-n">4</span>
            <div>
              <h3>Say what it means</h3>
              <p>
                A plain summary with each finding rated for urgency, written from the readings and in the units you
                prefer. Detailed enough for a technician, readable by anyone.
              </p>
            </div>
          </li>
        </ol>
      </section>

      <section className="mk-close">
        <h2>It runs next to the car</h2>
        <p>
          On a laptop or a phone, with a reader that costs less than a tank of fuel. Every fault carries its own
          explanation rather than a code you have to look up, nothing is shared between accounts, and an owner can be
          shown their own vehicle without being handed the keys to anybody else's.
        </p>
        <button className="primary" onClick={onSignIn}>
          Sign in
        </button>
      </section>

      <footer className="mk-footer">
        <span>Bay One — by Concourse Works, operated by OpenCircle</span>
      </footer>
    </div>
  );
}

/**
 * What the scan is actually doing: many readings, one answer per question.
 *
 * The same convergence idea as the studio's own site, in Bay One's green. It
 * earns its place because the product really does work this way — nine sources
 * collapsing into the four questions somebody actually asks — and because the
 * honest state is part of it. An answer with nothing behind it is drawn hollow
 * rather than omitted, which matches what the app already says out loud when a
 * service interval has no odometer reading to judge against.
 *
 * Laid out from numbers rather than hand-placed, so the threads stay attached
 * to their endpoints if any of this is retuned later.
 */
function ConvergenceArt() {
  const questions = [
    { label: 'Is it safe to drive?', state: 'found' },
    { label: 'What service is due?', state: 'found' },
    { label: 'Any open recalls?', state: 'found' },
    { label: 'What parts will it need?', state: 'found' },
    { label: 'How far has it gone?', state: 'thin' },
  ];

  const sources = [
    'STORED CODES', 'PENDING', 'PERMANENT', 'FREEZE FRAME', 'READINESS',
    'MODE 06', 'VIN DECODE', 'RECALLS', 'SCHEDULE', 'PARTS', 'ODOMETER',
  ];

  const W = 560;
  const H = 358;
  const markerX = 252;
  const top = 54;
  const gap = 56;

  // A settled scatter rather than a random one, so the cloud reads as data
  // instead of noise. Angles and radii are derived from the index.
  const nodes = Array.from({ length: 84 }, (_, i) => {
    const a = i * 2.399;
    const r = 26 + Math.sqrt(i / 84) * 128;
    return { x: 356 + Math.cos(a) * r * 0.95, y: 170 + Math.sin(a) * r * 0.92, big: i % 7 === 0 };
  });

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="mk-art mk-art-hero" role="presentation">
      <text x="0" y="16" className="mk-art-label">11 readings → the answers you need</text>

      {/* Threads. Every node reaches for a question, so the cloud visibly
          resolves into the column rather than floating beside it. */}
      <g>
        {nodes.map((n, i) => {
          const q = i % questions.length;
          const y = top + q * gap;
          const faint = questions[q].state === 'thin';
          return (
            <path
              key={i}
              // Both control points stay right of the marker column, so no
              // curve bows left across the question text. Without the clamp a
              // node on the near edge of the cloud put its control point well
              // left of the markers and the thread crossed the words.
              d={`M ${n.x} ${n.y} C ${Math.max(n.x - 70, markerX + 34)} ${n.y}, ${markerX + 74} ${y}, ${markerX + 12} ${y}`}
              fill="none"
              stroke="var(--brand)"
              strokeWidth={faint ? 0.7 : 1}
              opacity={faint ? 0.15 : 0.46}
            />
          );
        })}
      </g>

      <g>
        {nodes.map((n, i) => (
          <circle key={i} cx={n.x} cy={n.y} r={n.big ? 2.6 : 1.5} fill="var(--brand)" opacity={n.big ? 1 : 0.55} />
        ))}
      </g>

      {/* A few sources named, so it is legible as evidence rather than decoration. */}
      {sources.slice(0, 7).map((label, i) => (
        <text key={label} x={W - 4} y={48 + i * 42} className="mk-art-source" textAnchor="end">
          {label}
        </text>
      ))}

      {questions.map((q, i) => {
        const y = top + i * gap;
        return (
          <g key={q.label}>
            <text x={markerX - 16} y={y + 5} className="mk-art-question" textAnchor="end">
              {q.label}
            </text>
            <rect
              x={markerX - 6}
              y={y - 6}
              width="12"
              height="12"
              fill={q.state === 'found' ? 'var(--brand)' : 'none'}
              stroke="var(--brand)"
              strokeWidth="1.8"
            />
          </g>
        );
      })}

      <text x="0" y={H - 8} className="mk-art-label">
        ■ answered from the scan &nbsp;&nbsp; □ not enough data yet
      </text>
    </svg>
  );
}

/* One drawing per claim. Small, single-weight, and each one states the claim
   rather than decorating it: a timeline, a convergence, a translation, a
   boundary. Drawn to the same 120x56 box so the four sit on a common baseline
   however the grid reflows. */

function HistoryArt() {
  // Scans along a timeline, the earlier ones still there.
  const xs = [10, 32, 54, 76, 104];
  return (
    <svg viewBox="0 0 120 56" className="mk-claim-art" role="presentation">
      <line x1="6" y1="40" x2="114" y2="40" stroke="var(--line)" strokeWidth="1.5" />
      {xs.map((x, i) => (
        <g key={x}>
          <line x1={x} y1="40" x2={x} y2={34 - i * 4} stroke="var(--brand)" strokeWidth="1.5" opacity={0.35 + i * 0.16} />
          <circle cx={x} cy={34 - i * 4} r={i === xs.length - 1 ? 4 : 2.6} fill="var(--brand)" opacity={0.35 + i * 0.16} />
        </g>
      ))}
    </svg>
  );
}

function ConvergeArt() {
  // Four sources into one VIN.
  const ys = [8, 22, 36, 50];
  return (
    <svg viewBox="0 0 120 56" className="mk-claim-art" role="presentation">
      {ys.map((y) => (
        <path key={y} d={`M 8 ${y} C 46 ${y}, 52 28, 78 28`} fill="none" stroke="var(--brand)" strokeWidth="1.4" opacity="0.55" />
      ))}
      {ys.map((y) => (
        <circle key={y} cx="8" cy={y} r="3" fill="var(--brand)" opacity="0.55" />
      ))}
      <rect x="80" y="19" width="34" height="18" rx="4" fill="var(--brand)" />
      <text x="97" y="31" textAnchor="middle" className="mk-claim-art-text">VIN</text>
    </svg>
  );
}

function TranslateArt() {
  // Hex on the left, sentences on the right.
  return (
    <svg viewBox="0 0 120 56" className="mk-claim-art" role="presentation">
      {[0, 1, 2, 3].map((r) =>
        [0, 1, 2].map((c) => (
          <rect key={`${r}-${c}`} x={6 + c * 13} y={8 + r * 11} width="9" height="5" rx="1"
            fill="var(--ink-3)" opacity="0.35" />
        )),
      )}
      <path d="M 50 28 L 62 28 M 57 23 L 62 28 L 57 33" fill="none" stroke="var(--brand)" strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round" />
      {[0, 1, 2, 3].map((r) => (
        <rect key={r} x="68" y={9 + r * 11} width={r === 3 ? 26 : 46} height="4" rx="2" fill="var(--brand)"
          opacity={0.8 - r * 0.12} />
      ))}
    </svg>
  );
}

function ScopeArt() {
  // A fleet, with exactly one of them inside the owner's boundary.
  return (
    <svg viewBox="0 0 120 56" className="mk-claim-art" role="presentation">
      {[8, 30, 74, 96].map((x) => (
        <rect key={x} x={x} y="20" width="16" height="16" rx="3" fill="none" stroke="var(--ink-3)"
          strokeWidth="1.3" opacity="0.4" />
      ))}
      <rect x="46" y="12" width="24" height="32" rx="6" fill="none" stroke="var(--brand)" strokeWidth="1.4"
        strokeDasharray="4 3" />
      <rect x="52" y="20" width="16" height="16" rx="3" fill="var(--brand)" fillOpacity="0.16" stroke="var(--brand)" strokeWidth="1.5" />
    </svg>
  );
}

function ShopArt() {
  return (
    <svg viewBox="0 0 120 64" className="mk-art" role="presentation">
      {/* Three cars in a row: a fleet, one of them flagged. */}
      {[6, 44, 82].map((x, i) => (
        <g key={x}>
          <rect x={x} y="18" width="32" height="28" rx="6" fill="var(--brand)" fillOpacity="0.16" stroke="var(--brand)" strokeWidth="1.5" />
          <rect x={x + 6} y="24" width="20" height="8" rx="2" fill="var(--brand)" opacity="0.45" />
          <circle cx={x + 10} cy="46" r="4" fill="var(--ink-3)" />
          <circle cx={x + 22} cy="46" r="4" fill="var(--ink-3)" />
          {i === 1 && <circle cx={x + 28} cy="16" r="6" fill="var(--amber)" />}
        </g>
      ))}
    </svg>
  );
}

function MemberArt() {
  return (
    <svg viewBox="0 0 120 64" className="mk-art" role="presentation">
      {/* One car, and a boundary around it. */}
      <rect x="8" y="8" width="104" height="48" rx="10" fill="none" stroke="var(--line)" strokeDasharray="5 5" strokeWidth="1.5" />
      <rect x="42" y="20" width="36" height="30" rx="6" fill="var(--brand)" fillOpacity="0.16" stroke="var(--brand)" strokeWidth="1.5" />
      <rect x="49" y="26" width="22" height="9" rx="2" fill="var(--brand)" opacity="0.45" />
      <circle cx="52" cy="50" r="4" fill="var(--ink-3)" />
      <circle cx="68" cy="50" r="4" fill="var(--ink-3)" />
    </svg>
  );
}
